import posthog from "posthog-js";
import { hasAnalyticsConsent } from "@/lib/consent";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";

let analyticsReady = false;

function canCapture(): boolean {
  return Boolean(POSTHOG_KEY) && analyticsReady && hasAnalyticsConsent();
}

/** Call on boot and after consent changes. Never loads PostHog without "all". */
export function syncAnalyticsWithConsent(): void {
  if (!POSTHOG_KEY) return;

  if (hasAnalyticsConsent()) {
    if (!analyticsReady) {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: true,
        persistence: "localStorage+cookie",
      });
      analyticsReady = true;
    }
    try {
      posthog.opt_in_capturing();
    } catch {
      // opt_in may throw if not fully ready — init already enables capture
    }
    return;
  }

  // Essential only (or no choice yet): do not init; opt out if previously inited
  if (analyticsReady) {
    try {
      posthog.opt_out_capturing();
      posthog.reset();
    } catch {
      // ignore
    }
  }
}

/** @deprecated Prefer syncAnalyticsWithConsent — kept for call-site clarity */
export function initAnalytics(): void {
  syncAnalyticsWithConsent();
}

export function identifyUser(userId: string, properties?: Record<string, any>) {
  if (!canCapture()) return;
  posthog.identify(userId, properties);
}

export function trackEvent(event: string, properties?: Record<string, any>) {
  if (!canCapture()) return;
  posthog.capture(event, properties);
}

export type ConversionType = "waitlist" | "signup";

/** Conversion completed (waitlist join, signup). Consent-gated like every capture. */
export function trackConversion(
  type: ConversionType,
  properties?: Record<string, string | number | boolean | null>,
) {
  if (!canCapture()) return;
  posthog.capture("conversion_completed", { conversion_type: type, ...properties });
}

export type SeoPageType =
  | "landing"
  | "discover"
  | "festival_guide"
  | "endeavor_index"
  | "festival_detail"
  | "route_detail"
  | "shared_trip";

export interface SeoPageviewProps {
  pageType: SeoPageType;
  entityId?: string | number | null;
  entitySlug?: string | null;
  entityName?: string | null;
}

export function trackSeoPageview(props: SeoPageviewProps) {
  if (!canCapture()) return;
  if (typeof window === "undefined") return;
  const { pageType, entityId, entitySlug, entityName } = props;
  posthog.capture("seo_pageview", {
    page_type: pageType,
    entity_id: entityId ?? null,
    entity_slug: entitySlug ?? null,
    entity_name: entityName ?? null,
    path: window.location.pathname,
    referrer: document.referrer || null,
    referrer_host: document.referrer ? safeHost(document.referrer) : null,
  });
}

export interface SeoCtaClickProps extends SeoPageviewProps {
  cta: string;
}

export function trackSeoCtaClick(props: SeoCtaClickProps) {
  if (!canCapture()) return;
  if (typeof window === "undefined") return;
  const { pageType, entityId, entitySlug, entityName, cta } = props;
  posthog.capture("seo_cta_click", {
    page_type: pageType,
    entity_id: entityId ?? null,
    entity_slug: entitySlug ?? null,
    entity_name: entityName ?? null,
    cta,
    path: window.location.pathname,
  });
}

function safeHost(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

export function resetAnalytics() {
  if (!POSTHOG_KEY) return;
  if (!analyticsReady) return;
  posthog.reset();
}

/** Exported for tests */
export function __resetAnalyticsReadyForTests() {
  analyticsReady = false;
}
